import Box from '@mui/material/Box';
import { useEffect, useMemo, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/AddOutlined';
import PrintIcon from '@mui/icons-material/PrintOutlined';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import SearchableSelect from '@/components/common/SearchableSelect';
import SearchableMultiSelect from '@/components/common/SearchableMultiSelect';
import StatusChip from '@/components/common/StatusChip';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { IfPermitted } from '@/components/auth/RequirePermission';
import { useProcessConfiguration } from '@/hooks/useProcessConfiguration';
import { PERMISSIONS } from '@/types/auth';
import type { CarrierDto } from '@/types/materials';
import type {
  CreateLoadingTaskRequest,
  DispatchOrderOptionDto,
  LoadingCandidateMaterialIssueLineDto,
  LoadingCandidatePackageDto,
  LoadingTaskDto,
} from '@/types/outbound';
import LoadingSlipDialog from './LoadingSlipDialog';

export default function LoadingPage() {
  const { t } = useTranslation('loading');
  const queryClient = useQueryClient();

  const { data: processConfiguration } = useProcessConfiguration();
  // Packing skipped entirely for this company means there are no Packages to select from — the
  // Loading screen falls back to already-posted Dispatch Material Issue lines instead, with the
  // same "pick which dispatch orders, then check off specific rows" flow either way.
  const packingMode = processConfiguration?.packingEnabled ?? true;
  // With Barcode Required on, checking a row is scan-driven only (same pattern as Material
  // Issue/Putaway) — with it off, the original manual-checkbox selection is unchanged.
  const barcodeMandatory = processConfiguration?.barcodeMandatory ?? false;

  const { data: tasks = [], isLoading } = useQuery({
    queryKey: ['loading-tasks'],
    queryFn: async () => (await apiClient.get<LoadingTaskDto[]>('/loading-tasks')).data,
  });
  const { data: carriers = [] } = useQuery({
    queryKey: ['carriers'],
    queryFn: async () => (await apiClient.get<CarrierDto[]>('/carriers')).data,
  });

  const { data: availablePackingOrders = [] } = useQuery({
    queryKey: ['loading-tasks', 'available-dispatch-orders'],
    queryFn: async () => (await apiClient.get<DispatchOrderOptionDto[]>('/loading-tasks/available-dispatch-orders')).data,
    enabled: packingMode,
  });
  const { data: availableMaterialIssueOrders = [] } = useQuery({
    queryKey: ['loading-tasks', 'available-dispatch-orders-material-issue'],
    queryFn: async () => (await apiClient.get<DispatchOrderOptionDto[]>('/loading-tasks/available-dispatch-orders-material-issue')).data,
    enabled: !packingMode,
  });
  const availableDispatchOrders = packingMode ? availablePackingOrders : availableMaterialIssueOrders;

  const [open, setOpen] = useState(false);
  const [carrierId, setCarrierId] = useState('');
  const [vehicleNo, setVehicleNo] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [ewayBillNo, setEwayBillNo] = useState('');
  const [dispatchOrderIds, setDispatchOrderIds] = useState<string[]>([]);
  const [selectedPackageIds, setSelectedPackageIds] = useState<string[]>([]);
  const [selectedLineIds, setSelectedLineIds] = useState<string[]>([]);
  const [partialConfirmOpen, setPartialConfirmOpen] = useState(false);
  const [slipTask, setSlipTask] = useState<LoadingTaskDto | null>(null);
  // The one shared scan input for barcode mode — replaces manual checkbox-ticking. Typing/scanning
  // a value that exactly matches a not-yet-selected row's barcode auto-checks that row and clears
  // this field for the next scan (see tryMatchScan). Mirrors Material Issue/PutawayPage exactly.
  const [scanInput, setScanInput] = useState('');
  // Scanned row ids, most-recently-scanned first — drives "scanned rows float to the top".
  const [scanOrder, setScanOrder] = useState<string[]>([]);

  // One row per still-unloaded Package across every selected dispatch order — see
  // LoadingService.GetCandidatePackagesAsync. Array query params need indexes:null so axios sends
  // ?dispatchOrderIds=a&dispatchOrderIds=b instead of its default dispatchOrderIds[]=a&...[]=b,
  // which is what ASP.NET Core's [FromQuery] Guid[] binder actually understands.
  const { data: candidatePackages = [], isFetching: packagesLoading } = useQuery({
    queryKey: ['loading-tasks', 'candidate-packages', dispatchOrderIds],
    queryFn: async () =>
      (
        await apiClient.get<LoadingCandidatePackageDto[]>('/loading-tasks/candidate-packages', {
          params: { dispatchOrderIds },
          paramsSerializer: { indexes: null },
        })
      ).data,
    enabled: packingMode && dispatchOrderIds.length > 0,
  });
  const { data: candidateLines = [], isFetching: linesLoading } = useQuery({
    queryKey: ['loading-tasks', 'candidate-material-issue-lines', dispatchOrderIds],
    queryFn: async () =>
      (
        await apiClient.get<LoadingCandidateMaterialIssueLineDto[]>('/loading-tasks/candidate-material-issue-lines', {
          params: { dispatchOrderIds },
          paramsSerializer: { indexes: null },
        })
      ).data,
    enabled: !packingMode && dispatchOrderIds.length > 0,
  });
  const candidatesLoading = packingMode ? packagesLoading : linesLoading;

  // Nothing is pre-checked — the operator picks exactly which rows this load carries; the rest
  // stay available for a later Loading Task (partial loading).
  useEffect(() => {
    setSelectedPackageIds([]);
    setScanOrder([]);
  }, [candidatePackages]);
  useEffect(() => {
    setSelectedLineIds([]);
    setScanOrder([]);
  }, [candidateLines]);

  const allPackagesSelected = candidatePackages.length > 0 && selectedPackageIds.length === candidatePackages.length;
  const somePackagesSelected = selectedPackageIds.length > 0 && !allPackagesSelected;
  const allLinesSelected = candidateLines.length > 0 && selectedLineIds.length === candidateLines.length;
  const someLinesSelected = selectedLineIds.length > 0 && !allLinesSelected;

  const togglePackage = (packageId: string, checked: boolean) =>
    setSelectedPackageIds((prev) => (checked ? [...prev, packageId] : prev.filter((id) => id !== packageId)));
  const toggleLine = (lineId: string, checked: boolean) =>
    setSelectedLineIds((prev) => (checked ? [...prev, lineId] : prev.filter((id) => id !== lineId)));

  // Unified view over whichever candidate list is actually active (packages vs material-issue
  // lines) so the scan logic below doesn't need two near-identical copies of itself.
  const activeCandidateIds: { id: string; barcode: string | null }[] = packingMode
    ? candidatePackages.map((p) => ({ id: p.packageId, barcode: p.barcode }))
    : candidateLines.map((l) => ({ id: l.lineId, barcode: l.barcode }));
  const selectedIds = packingMode ? selectedPackageIds : selectedLineIds;
  const toggleSelected = packingMode ? togglePackage : toggleLine;

  // Tries to match `value` against a not-yet-selected row's barcode — on every keystroke (a
  // scanner types the full value near-instantly) and explicitly on Enter (fallback for a scanner
  // that sends Enter, or manual typing). On a match: the row is selected and floated to the top;
  // the field clears for the next scan.
  const tryMatchScan = (value: string): boolean => {
    const trimmed = value.trim().toUpperCase();
    if (!trimmed) {
      return false;
    }
    const match = activeCandidateIds.find((c) => !selectedIds.includes(c.id) && (c.barcode ?? '').toUpperCase() === trimmed);
    if (!match) {
      return false;
    }
    toggleSelected(match.id, true);
    setScanOrder((prev) => [match.id, ...prev.filter((id) => id !== match.id)]);
    setScanInput('');
    return true;
  };

  const handleScanInputChange = (value: string) => {
    if (!tryMatchScan(value)) {
      setScanInput(value);
    }
  };

  const handleScanKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      tryMatchScan(scanInput);
    }
  };

  // Scanned rows float to the top (most-recent first); everything else keeps its original order —
  // only applied in barcode mode, same as every other screen with this pattern.
  const orderedCandidatePackages = useMemo(() => {
    if (!barcodeMandatory) {
      return candidatePackages;
    }
    const scannedFirst = scanOrder.map((id) => candidatePackages.find((p) => p.packageId === id)).filter((p): p is NonNullable<typeof p> => Boolean(p));
    const rest = candidatePackages.filter((p) => !scanOrder.includes(p.packageId));
    return [...scannedFirst, ...rest];
  }, [candidatePackages, scanOrder, barcodeMandatory]);
  const orderedCandidateLines = useMemo(() => {
    if (!barcodeMandatory) {
      return candidateLines;
    }
    const scannedFirst = scanOrder.map((id) => candidateLines.find((l) => l.lineId === id)).filter((l): l is NonNullable<typeof l> => Boolean(l));
    const rest = candidateLines.filter((l) => !scanOrder.includes(l.lineId));
    return [...scannedFirst, ...rest];
  }, [candidateLines, scanOrder, barcodeMandatory]);

  const scannableCandidateCount = activeCandidateIds.filter((c) => c.barcode).length;
  const scannedCandidateCount = activeCandidateIds.filter((c) => c.barcode && selectedIds.includes(c.id)).length;

  const createMutation = useMutation({
    mutationFn: () => {
      const payload: CreateLoadingTaskRequest = {
        packingTaskId: null,
        pickListId: null,
        carrierId: carrierId || null,
        vehicleNo: vehicleNo || null,
        invoiceNumber: invoiceNumber || null,
        ewayBillNo: ewayBillNo || null,
        packageIds: packingMode ? selectedPackageIds : [],
        materialIssueLineIds: packingMode ? [] : selectedLineIds,
      };
      return apiClient.post('/loading-tasks', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loading-tasks'] });
      setOpen(false);
      setPartialConfirmOpen(false);
    },
  });

  const confirmMutation = useMutation({
    mutationFn: (id: string) => apiClient.post(`/loading-tasks/${id}/confirm`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['loading-tasks'] }),
  });

  // Only the material-issue path (Packing skipped) prompts before a partial submission — the
  // operator is committing already-issued stock straight onto a truck with no packing step to
  // double-check quantities against, so a deliberate confirmation matters more here.
  const isPartialLineSelection = !packingMode && selectedLineIds.length > 0 && selectedLineIds.length < candidateLines.length;
  const handleSave = () => {
    if (isPartialLineSelection) {
      setPartialConfirmOpen(true);
      return;
    }
    createMutation.mutate();
  };

  const columns: DataTableColumn<LoadingTaskDto>[] = [
    { field: 'loadingTaskNumber', headerName: t('table.number') },
    {
      field: 'dispatchOrders',
      headerName: t('table.dispatchOrders'),
      render: (r) => (r.dispatchOrders.length > 0 ? r.dispatchOrders.map((o) => o.orderNumber).join(', ') : (r.pickListNumber ?? r.packingTaskNumber ?? '—')),
    },
    { field: 'carrierName', headerName: t('table.carrier'), render: (r) => r.carrierName ?? '—', filterable: true },
    { field: 'vehicleNo', headerName: t('table.vehicle'), render: (r) => r.vehicleNo ?? '—' },
    { field: 'invoiceNumber', headerName: t('table.invoiceNumber'), render: (r) => r.invoiceNumber ?? '—' },
    { field: 'ewayBillNo', headerName: t('table.ewayBillNo'), render: (r) => r.ewayBillNo ?? '—' },
    { field: 'status', headerName: t('table.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    {
      field: 'action', headerName: '', render: (r) => (
        <IfPermitted permission={PERMISSIONS.LOADING_EXECUTE}>
          <Stack direction="row" spacing={1}>
            {r.status === 'Pending' && (
              <Button size="small" variant="outlined" disabled={confirmMutation.isPending} onClick={() => confirmMutation.mutate(r.id)}>
                {t('confirm')}
              </Button>
            )}
            {r.status === 'Loaded' && (
              <Button size="small" variant="outlined" startIcon={<PrintIcon fontSize="small" />} onClick={() => setSlipTask(r)}>
                {t('slip.print')}
              </Button>
            )}
          </Stack>
        </IfPermitted>
      ),
    },
  ];

  const openDialog = () => {
    setCarrierId('');
    setVehicleNo('');
    setInvoiceNumber('');
    setEwayBillNo('');
    setDispatchOrderIds([]);
    setSelectedPackageIds([]);
    setSelectedLineIds([]);
    setScanInput('');
    setScanOrder([]);
    queryClient.invalidateQueries({ queryKey: ['loading-tasks', 'available-dispatch-orders'] });
    queryClient.invalidateQueries({ queryKey: ['loading-tasks', 'available-dispatch-orders-material-issue'] });
    setOpen(true);
  };

  const selectionCount = packingMode ? selectedPackageIds.length : selectedLineIds.length;

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        actions={
          <IfPermitted permission={PERMISSIONS.LOADING_EXECUTE}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openDialog}>
              {t('add')}
            </Button>
          </IfPermitted>
        }
      />

      <DataTable columns={columns} rows={tasks} loading={isLoading} searchable getRowId={(r) => r.id} exportFileName="loading-tasks" />

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t('add')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <SearchableMultiSelect
              label={t('form.dispatchOrders')}
              value={dispatchOrderIds}
              onChange={setDispatchOrderIds}
              fullWidth
              helperText={packingMode ? t('form.dispatchOrdersHelper') : t('form.dispatchOrdersHelperMaterialIssue')}
              options={availableDispatchOrders.map((o) => ({
                value: o.id,
                label: `${o.orderNumber} — ${o.destinationType === 'Warehouse' ? `→ ${o.destinationWarehouseName}` : o.customerName}`,
              }))}
            />

            <Stack spacing={1}>
              <Typography variant="subtitle2">{packingMode ? t('form.packagesTitle') : t('form.materialIssueTitle')}</Typography>
              {dispatchOrderIds.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {packingMode ? t('form.selectDispatchOrdersFirst') : t('form.selectDispatchOrdersFirstMaterialIssue')}
                </Typography>
              ) : packingMode ? (
                !candidatesLoading && candidatePackages.length === 0 ? (
                  <Alert severity="info">{t('form.noPackagesAvailable')}</Alert>
                ) : (
                  <>
                    {barcodeMandatory && (
                      <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        <TextField
                          label={t('form.scanBarcode')}
                          value={scanInput}
                          onChange={(e) => handleScanInputChange(e.target.value)}
                          onKeyDown={handleScanKeyDown}
                          helperText={t('form.scanBarcodeHelperPackages')}
                          fullWidth
                          autoFocus
                        />
                        <Chip
                          label={t('form.scannedOfTotal', { scanned: scannedCandidateCount, total: scannableCandidateCount })}
                          color={scannedCandidateCount >= scannableCandidateCount ? 'success' : 'warning'}
                          sx={{ mt: 1.25, fontWeight: 700, minWidth: 92, flexShrink: 0 }}
                        />
                      </Stack>
                    )}
                    <Box sx={{ overflowX: 'auto' }}>
                      <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox">
                            <Checkbox
                              indeterminate={somePackagesSelected}
                              checked={allPackagesSelected}
                              disabled={barcodeMandatory}
                              onChange={(e) => setSelectedPackageIds(e.target.checked ? candidatePackages.map((p) => p.packageId) : [])}
                            />
                          </TableCell>
                          <TableCell>{t('form.dispatchNo')}</TableCell>
                          <TableCell>{t('form.packageNo')}</TableCell>
                          <TableCell>{t('form.materialCode')}</TableCell>
                          <TableCell>{t('form.materialName')}</TableCell>
                          {barcodeMandatory && <TableCell>{t('form.barcode')}</TableCell>}
                          <TableCell align="right">{t('form.qty')}</TableCell>
                          <TableCell align="right">{t('form.weight')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(barcodeMandatory ? orderedCandidatePackages : candidatePackages).map((pkg) => {
                          const checked = selectedPackageIds.includes(pkg.packageId);
                          const scanned = barcodeMandatory && checked;
                          return (
                            <TableRow key={pkg.packageId} sx={scanned ? { bgcolor: 'success.50' } : undefined}>
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={checked}
                                  disabled={barcodeMandatory}
                                  onChange={(e) => togglePackage(pkg.packageId, e.target.checked)}
                                />
                              </TableCell>
                              <TableCell>{pkg.dispatchOrderNumber}</TableCell>
                              <TableCell>{pkg.packageNo}</TableCell>
                              <TableCell>{pkg.materialCode}</TableCell>
                              <TableCell>{pkg.materialName}</TableCell>
                              {barcodeMandatory && (
                                <TableCell>
                                  {pkg.barcode ? (
                                    <Typography variant="body2" color={checked ? 'success.main' : 'text.secondary'}>
                                      {pkg.barcode}{checked ? ` — ${t('form.scanned')}` : ''}
                                    </Typography>
                                  ) : (
                                    <Typography variant="caption" color="error">
                                      {t('form.noBarcodeBlocked')}
                                    </Typography>
                                  )}
                                </TableCell>
                              )}
                              <TableCell align="right">{pkg.qty}</TableCell>
                              <TableCell align="right">{pkg.weight ?? '—'}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                      </Table>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('form.selectedOf', { selected: selectedPackageIds.length, total: candidatePackages.length })}
                    </Typography>
                  </>
                )
              ) : !candidatesLoading && candidateLines.length === 0 ? (
                <Alert severity="info">{t('form.noMaterialIssueLinesAvailable')}</Alert>
              ) : (
                <>
                  {barcodeMandatory && (
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                      <TextField
                        label={t('form.scanBarcode')}
                        value={scanInput}
                        onChange={(e) => handleScanInputChange(e.target.value)}
                        onKeyDown={handleScanKeyDown}
                        helperText={t('form.scanBarcodeHelperLines')}
                        fullWidth
                        autoFocus
                      />
                      <Chip
                        label={t('form.scannedOfTotal', { scanned: scannedCandidateCount, total: scannableCandidateCount })}
                        color={scannedCandidateCount >= scannableCandidateCount ? 'success' : 'warning'}
                        sx={{ mt: 1.25, fontWeight: 700, minWidth: 92, flexShrink: 0 }}
                      />
                    </Stack>
                  )}
                  <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            indeterminate={someLinesSelected}
                            checked={allLinesSelected}
                            disabled={barcodeMandatory}
                            onChange={(e) => setSelectedLineIds(e.target.checked ? candidateLines.map((l) => l.lineId) : [])}
                          />
                        </TableCell>
                        <TableCell>{t('form.dispatchNo')}</TableCell>
                        <TableCell>{t('form.materialCode')}</TableCell>
                        <TableCell>{t('form.materialName')}</TableCell>
                        <TableCell>{t('form.barcode')}</TableCell>
                        <TableCell align="right">{t('form.qty')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(barcodeMandatory ? orderedCandidateLines : candidateLines).map((line) => {
                        const checked = selectedLineIds.includes(line.lineId);
                        const scanned = barcodeMandatory && checked;
                        return (
                        <TableRow key={line.lineId} sx={scanned ? { bgcolor: 'success.50' } : undefined}>
                          <TableCell padding="checkbox">
                            <Checkbox checked={checked} disabled={barcodeMandatory} onChange={(e) => toggleLine(line.lineId, e.target.checked)} />
                          </TableCell>
                          <TableCell>{line.dispatchOrderNumber}</TableCell>
                          <TableCell>{line.materialCode}</TableCell>
                          <TableCell>{line.materialName}</TableCell>
                          <TableCell>
                            {line.barcode ? (
                              barcodeMandatory ? (
                                <Typography variant="body2" color={checked ? 'success.main' : 'text.secondary'}>
                                  {line.barcode}{checked ? ` — ${t('form.scanned')}` : ''}
                                </Typography>
                              ) : line.barcode
                            ) : barcodeMandatory ? (
                              <Typography variant="caption" color="error">
                                {t('form.noBarcodeBlocked')}
                              </Typography>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          <TableCell align="right">{line.qty}</TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                    </Table>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('form.selectedOfLines', { selected: selectedLineIds.length, total: candidateLines.length })}
                  </Typography>
                </>
              )}
            </Stack>

            <SearchableSelect
              label={t('form.carrier')} value={carrierId} onChange={setCarrierId} fullWidth
              options={[{ value: '', label: t('form.none') }, ...carriers.map((c) => ({ value: c.id, label: c.name }))]}
            />
            <TextField label={t('form.vehicleNo')} value={vehicleNo} onChange={(e) => setVehicleNo(e.target.value)} fullWidth />
            <TextField label={t('form.invoiceNumber')} value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} fullWidth />
            <TextField label={t('form.ewayBillNo')} value={ewayBillNo} onChange={(e) => setEwayBillNo(e.target.value)} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)}>{t('cancel')}</Button>
          <Button variant="contained" disabled={createMutation.isPending || selectionCount === 0} onClick={handleSave}>
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={partialConfirmOpen}
        title={t('partialConfirm.title')}
        message={t('partialConfirm.message', { selected: selectedLineIds.length, total: candidateLines.length })}
        confirmLabel={t('partialConfirm.confirm')}
        confirmColor="primary"
        loading={createMutation.isPending}
        onConfirm={() => createMutation.mutate()}
        onClose={() => setPartialConfirmOpen(false)}
      />

      <LoadingSlipDialog task={slipTask} onClose={() => setSlipTask(null)} />
    </>
  );
}
